import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, inject, input, output, signal } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { MobileVoiceCaptureService } from "../../platform/mobile-voice-capture.service";

export type MobileVoiceReportRecorderPhase = "idle" | "recording" | "processing";

@Component({
  selector: "mobile-voice-report-recorder",
  imports: [CommonModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./mobile-voice-report-recorder.component.html",
  styleUrl: "./mobile-voice-report-recorder.component.scss",
})
export class MobileVoiceReportRecorderComponent implements OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly voiceCapture = inject(MobileVoiceCaptureService);

  /** Libellé contexte (ex. nom du chantier). */
  readonly contextLabel = input<string | null>(null);
  /** Phase pilotée par le parent pendant l’appel API (`processing`). */
  readonly externalPhase = input<MobileVoiceReportRecorderPhase | null>(null);

  /** Émis à la fin de l’enregistrement avec le blob audio. */
  readonly finished = output<Blob>();
  /** Émis si le micro est indisponible. */
  readonly recordError = output<string>();

  protected readonly internalPhase = signal<MobileVoiceReportRecorderPhase>("idle");
  protected readonly elapsedSeconds = signal(0);
  protected readonly micDenied = signal(false);

  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private timerId: ReturnType<typeof setInterval> | null = null;

  protected phase(): MobileVoiceReportRecorderPhase {
    return this.externalPhase() ?? this.internalPhase();
  }

  protected async onPrimaryAction(): Promise<void> {
    const phase = this.phase();
    if (phase === "processing") {
      return;
    }
    if (phase === "recording") {
      this.stopRecording();
      return;
    }
    await this.startRecording();
  }

  ngOnDestroy(): void {
    this.cleanupMedia();
  }

  private async startRecording(): Promise<void> {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      this.micDenied.set(true);
      this.recordError.emit("mobile.voiceReport.recorder.error.unsupported");
      return;
    }

    const micGranted = await this.voiceCapture.ensureMicrophoneAccess();
    if (!micGranted) {
      this.micDenied.set(true);
      this.recordError.emit("mobile.voiceReport.recorder.error.micDenied");
      return;
    }

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      this.micDenied.set(true);
      this.recordError.emit("mobile.voiceReport.recorder.error.micDenied");
      return;
    }

    this.chunks = [];
    this.micDenied.set(false);
    const mimeType = this.resolveMimeType();
    this.mediaRecorder = mimeType
      ? new MediaRecorder(this.mediaStream, { mimeType })
      : new MediaRecorder(this.mediaStream);

    this.mediaRecorder.ondataavailable = (ev: BlobEvent) => {
      if (ev.data.size > 0) {
        this.chunks.push(ev.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      const blob = new Blob(this.chunks, { type: this.mediaRecorder?.mimeType || "audio/webm" });
      this.cleanupMedia();
      this.internalPhase.set("idle");
      this.stopTimer();
      this.finished.emit(blob);
    };

    this.mediaRecorder.start(250);
    this.internalPhase.set("recording");
    this.elapsedSeconds.set(0);
    this.startTimer();
  }

  private stopRecording(): void {
    if (this.mediaRecorder?.state === "recording") {
      this.mediaRecorder.stop();
    } else {
      this.cleanupMedia();
      this.internalPhase.set("idle");
      this.stopTimer();
    }
  }

  private resolveMimeType(): string | undefined {
    if (typeof MediaRecorder === "undefined") {
      return undefined;
    }
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
    return candidates.find((candidateType) => MediaRecorder.isTypeSupported(candidateType));
  }

  private startTimer(): void {
    this.stopTimer();
    this.timerId = setInterval(() => {
      this.elapsedSeconds.update((elapsedSeconds) => elapsedSeconds + 1);
    }, 1000);
    this.destroyRef.onDestroy(() => this.stopTimer());
  }

  private stopTimer(): void {
    if (this.timerId != null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private cleanupMedia(): void {
    this.stopTimer();
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      try {
        this.mediaRecorder.stop();
      } catch {
        // ignore
      }
    }
    this.mediaRecorder = null;
    this.mediaStream?.getTracks().forEach((track) => track.stop());
    this.mediaStream = null;
  }

  protected formatElapsed(): string {
    const total = this.elapsedSeconds();
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  protected primaryLabelKey(): string {
    const phase = this.phase();
    if (phase === "processing") {
      return "mobile.voiceReport.recorder.processing";
    }
    if (phase === "recording") {
      return "mobile.voiceReport.recorder.finish";
    }
    return "mobile.voiceReport.recorder.start";
  }

  protected primaryIcon(): string {
    const phase = this.phase();
    if (phase === "processing") {
      return "hourglass_top";
    }
    if (phase === "recording") {
      return "stop_circle";
    }
    return "mic";
  }
}
